import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, BadgeCheck, ShieldCheck, Wallet, Undo2, Headset, Users } from "lucide-react";
import { LabPricingSlider } from "@/components/LabPricingSlider";
import { BookDemoButton } from "@/components/BookDemo";

const GUARANTEES = [
    { icon: BadgeCheck, t: "No hidden fees", d: "The slider price is the whole price — hosting, updates and your white-label subdomain included." },
    { icon: ShieldCheck, t: "Low risk", d: "Monthly plans cancel any time before renewal. No lock-in contracts." },
    { icon: Wallet, t: "No upfront payments", d: "No setup fees. Onboarding and data import are on us." },
    { icon: Undo2, t: "3-month money-back guarantee", d: "If Toothmatrix doesn't deliver for your lab, you leave with a full refund." },
    { icon: Headset, t: "Premium 1-on-1 support", d: "A direct line to our team — free of charge, in English or Arabic." },
    { icon: Users, t: "Unlimited licenses included", d: "Every technician, driver and accountant — plus free practice portals for all your clinics." },
];

const FAQS = [
    { q: "How does usage-based pricing work?", a: "Pick your monthly case volume on the slider — that's your price, in USD. Every feature is included on every tier: unlimited users, white-label branding, and free practice portals for your clinics." },
    { q: "What counts as a case?", a: "Every case created in your lab during a billing month counts toward your volume. If you outgrow your tier we'll simply suggest the next one — nothing breaks, nothing is blocked." },
    { q: "Do I save with yearly billing?", a: "Yes — yearly billing saves 15% on every tier. Monthly plans are also available and cancel any time before renewal." },
    { q: "Are there setup fees or per-user charges?", a: "No. There are no setup fees, no per-seat charges and no hidden extras. Unlimited licenses are included — every technician, driver and accountant on your team." },
    { q: "What if it doesn't work out?", a: "You're covered by a 3-month money-back guarantee. If Toothmatrix doesn't deliver for your lab, you leave with a full refund." },
];

export default function Pricing() {
    return (
        <div data-testid="pricing-page" className="bg-slate-50 text-slate-900">
            {/* HERO + SLIDER */}
            <section className="relative bg-[#0B1120] text-white overflow-hidden tr-grain">
                <div className="absolute -top-32 -right-20 w-[460px] h-[460px] tr-glow bg-cyan-500/30" />
                <div className="absolute bottom-0 -left-32 w-[380px] h-[380px] tr-glow bg-teal-500/20" />
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 relative">
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                        <div>
                            <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-300/80">· Pricing · Usage-based · USD</div>
                            <h1 className="mt-3 font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05]">
                                Pay for what<br /><span className="text-cyan-300">you produce.</span>
                            </h1>
                            <p className="mt-6 text-white/70 max-w-xl text-lg leading-[1.7]">
                                Usage-based pricing in USD — pick your monthly case volume and that&rsquo;s your price.
                                Every feature on every tier. Yearly billing saves 15%.
                            </p>
                            <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="pricing-guarantees">
                                {GUARANTEES.map((g) => (
                                    <div key={g.t} className="rounded-2xl bg-white/[0.06] border border-white/10 p-5 backdrop-blur-sm" data-testid={`pricing-guarantee-${g.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                        <div className="flex items-center gap-2.5 mb-2">
                                            <g.icon size={16} className="text-emerald-400 shrink-0" />
                                            <div className="text-[14px] font-semibold text-white">{g.t}</div>
                                        </div>
                                        <p className="text-[12.5px] text-slate-300/90 leading-relaxed">{g.d}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="lg:sticky lg:top-24">
                            <LabPricingSlider />
                            <div className="mt-4 text-center text-[12.5px] text-white/50">
                                Producing more than 500 cases a month? <a href="mailto:orders@toothrocket.com" className="text-cyan-300 hover:underline font-medium">Talk to us</a> about a custom plan.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CLINICS ARE FREE */}
            <section className="bg-cyan-50/60 border-b border-cyan-100" data-testid="pricing-clinics-free">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-3">
                    <div className="text-[14px] text-slate-700">
                        <strong className="text-slate-900">A dental clinic?</strong> Toothrocket is <strong className="text-cyan-700">free for clinics</strong> — no subscriptions, no portal fees. You only pay for the work you order.
                    </div>
                    <Link to="/practice/register" data-testid="pricing-clinics-free-cta" className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-cyan-700 hover:text-cyan-500">
                        Register your practice free <ArrowRight size={13} />
                    </Link>
                </div>
            </section>

            {/* EVERY TIER INCLUDES */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="pricing-includes">
                <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-700 mb-3">· Every tier includes</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        "Live dashboard, Kanban + WIP limits",
                        "Scan Inbox for every clinic scanner",
                        "Invoices, prepaid credits & multi-currency finance",
                        "Payroll & doctor commissions",
                        "White-label branding on your subdomain",
                        "Free practice portals for your clinics",
                        "Daily backups & tenant data isolation",
                        "Priority email + 1-on-1 support",
                    ].map((f) => (
                        <div key={f} className="rounded-xl bg-white border border-slate-200 px-4 py-3.5 text-[13.5px] text-slate-700 flex items-start gap-2">
                            <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" /> {f}
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-16" data-testid="pricing-faq">
                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-700">· FAQ</div>
                <h2 className="mt-2 font-display text-3xl lg:text-4xl tracking-tight text-slate-900">Common questions</h2>
                <div className="mt-8 space-y-5">
                    {FAQS.map((f) => (
                        <div key={f.q} className="rounded-2xl border border-slate-200 bg-white p-5">
                            <div className="font-display text-[17px] text-slate-900 tracking-tight">{f.q}</div>
                            <div className="mt-2 text-[14px] text-slate-600 leading-[1.7]">{f.a}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0B1120] text-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 text-center">
                    <h2 className="font-display text-3xl lg:text-5xl tracking-tight">See it on your own workflows first.</h2>
                    <p className="mt-4 text-white/70 max-w-2xl mx-auto">Book a 30-minute demo — we&rsquo;ll walk through your lab&rsquo;s real cases, not a canned deck.</p>
                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                        <BookDemoButton testid="pricing-bottom-cta"
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-cyan-400 text-[#0B1120] font-medium text-[14px] hover:bg-cyan-300 transition" />
                        <Link to="/solutions/dental-labs" data-testid="pricing-explore-link" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-white/20 text-white text-[14px] font-medium hover:bg-white/10">
                            Explore the product <ArrowRight size={15} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
